// Normalized invoice-line categories shared by every client adapter.
// Static enum for Stage 1 (see plan open question #3 re: Firestore-configurable later).
export const LINE_CATEGORIES = Object.freeze({
  SALARY_REIMBURSEMENT: "SALARY_REIMBURSEMENT",
  OVERTIME_RECOVERY: "OVERTIME_RECOVERY",
  BONUS_RECOVERY: "BONUS_RECOVERY",
  LEAVE_ENCASHMENT_RECOVERY: "LEAVE_ENCASHMENT_RECOVERY",
  EOBI_RECOVERY: "EOBI_RECOVERY",
  PESSI_RECOVERY: "PESSI_RECOVERY",
  INSURANCE_RECOVERY: "INSURANCE_RECOVERY",
  // GLI is paid by ALM as one annual lump sum but recovered from clients
  // monthly — a cash-flow timing difference, not a claims-based surplus like
  // health insurance. Kept as its own simple pass-through category, separate
  // from INSURANCE_RECOVERY (health insurance), and deliberately outside the
  // Health Insurance Control's received-vs-claims-paid reconciliation.
  GLI_RECOVERY: "GLI_RECOVERY",
  GRATUITY_RECOVERY: "GRATUITY_RECOVERY",
  FUEL_RECOVERY: "FUEL_RECOVERY",
  OTHER_EMPLOYEE_RECOVERY: "OTHER_EMPLOYEE_RECOVERY",
  OTHER_REIMBURSEMENT: "OTHER_REIMBURSEMENT",
  SERVICE_CHARGE: "SERVICE_CHARGE",
  MANAGEMENT_FEE: "MANAGEMENT_FEE",
  WHT_RECOVERY: "WHT_RECOVERY",
  OTHER_REVENUE: "OTHER_REVENUE",
  GST: "GST"
});

export const LINE_CATEGORY_LIST = Object.values(LINE_CATEGORIES);

// Categories that make up recognized service-charge revenue (requirement 9/16):
// service-charge revenue must ONLY ever be summed from these — never derived
// from cash received minus taxes/expenses.
export const REVENUE_RECOGNITION_CATEGORIES = Object.freeze([
  LINE_CATEGORIES.SERVICE_CHARGE,
  LINE_CATEGORIES.MANAGEMENT_FEE,
  LINE_CATEGORIES.OTHER_REVENUE
]);

export function isKnownCategory(category) {
  return LINE_CATEGORY_LIST.includes(category);
}

// Builds one normalized line, throwing if the category isn't in the enum so an
// adapter typo surfaces immediately instead of silently becoming "unclassified".
export function makeLine({ category, description, amount, sourceField = null, employeeCode = null }) {
  if (!isKnownCategory(category)) {
    throw new Error(`Unknown invoice line category: ${category}`);
  }
  return {
    category,
    description: description || category,
    amount: Number(amount || 0),
    sourceField,
    employeeCode
  };
}

export function sumRevenueLines(lines) {
  return lines
    .filter((line) => REVENUE_RECOGNITION_CATEGORIES.includes(line.category))
    .reduce((sum, line) => sum + Number(line.amount || 0), 0);
}

// Groups a set of normalized lines by category, summing amounts — used by
// the Monthly P&L's revenue breakdown so the report shows composition
// (service charge vs. salary recovery vs. GST, etc.), not just one total.
export function sumLinesByCategory(lines) {
  const totals = {};
  lines.forEach((line) => {
    totals[line.category] = Math.round((totals[line.category] || 0) + Number(line.amount || 0));
  });
  return totals;
}
